/**
 * Turkish Phone Number Formatter Utility
 * Formats phone numbers to Turkish format: 0532 123 45 67
 */

/**
 * Formats a raw phone input to Turkish format (0532 123 45 67)
 * Handles input as user types, adding spaces automatically
 * @param {string} value - Raw input value
 * @returns {string} - Formatted phone number
 */
export const formatTurkishPhone = (value) => {
  if (!value) return '';

  // Remove all non-digit characters
  let digits = value?.replace(/\D/g, '');

  // If starts with +90, remove country code
  if (digits?.startsWith('90') && digits?.length > 10) {
    digits = '0' + digits?.slice(2);
  }

  // Ensure starts with 0
  if (digits?.length > 0 && digits?.[0] !== '0') {
    digits = '0' + digits;
  }

  // Limit to 11 digits (0 + 10 digit number)
  digits = digits?.slice(0, 11);

  // Apply Turkish format: 0532 123 45 67
  // Groups: 4 + 3 + 2 + 2
  let formatted = '';
  if (digits?.length <= 4) {
    formatted = digits;
  } else if (digits?.length <= 7) {
    formatted = digits?.slice(0, 4) + ' ' + digits?.slice(4);
  } else if (digits?.length <= 9) {
    formatted = digits?.slice(0, 4) + ' ' + digits?.slice(4, 7) + ' ' + digits?.slice(7);
  } else {
    formatted = digits?.slice(0, 4) + ' ' + digits?.slice(4, 7) + ' ' + digits?.slice(7, 9) + ' ' + digits?.slice(9, 11);
  }

  return formatted;
};

/**
 * Strips formatting from a Turkish phone number, returns digits only
 * @param {string} value - Formatted phone number
 * @returns {string} - Digits only
 */
export const stripPhoneFormatting = (value) => {
  if (!value) return '';
  return value?.replace(/\D/g, '');
};

/**
 * Validates a Turkish phone number
 * Valid formats: 0532 123 45 67, 05321234567, +905321234567
 * @param {string} value - Phone number to validate
 * @param {boolean} required - Whether the field is required
 * @returns {string|null} - Error message or null if valid
 */
export const validateTurkishPhone = (value, required = true) => {
  const trimmed = value?.trim() || '';

  if (!trimmed) {
    return required ? 'Telefon numarası gereklidir' : null;
  }

  let digits = stripPhoneFormatting(trimmed);

  // Must be exactly 11 digits starting with 0, or 10 digits (without leading 0)
  if (digits?.length === 11 && digits?.startsWith('0')) {
    // Valid: 05XX XXX XX XX
    const operatorCode = digits?.slice(1, 4);
    const validPrefixes = ['50', '51', '52', '53', '54', '55', '56', '57', '58', '59'];
    const prefix = operatorCode?.slice(0, 2);
    if (!validPrefixes?.includes(prefix)) {
      return 'Geçerli bir Türk cep telefonu numarası giriniz (05XX XXX XX XX)';
    }
    return null;
  }

  if (digits?.length === 10 && !digits?.startsWith('0')) {
    return null; // Accept 10-digit without leading 0
  }

  return 'Telefon numarası 11 haneli olmalıdır (0532 123 45 67)';
};

/**
 * Handles phone input change event with auto-formatting
 * Use this in onChange handlers
 * @param {Event} e - Input change event
 * @param {Function} setter - State setter function (receives formatted value)
 */
export const handlePhoneInputChange = (e, setter) => {
  let formatted = formatTurkishPhone(e?.target?.value);
  setter(formatted);
};

export default {
  formatTurkishPhone,
  stripPhoneFormatting,
  validateTurkishPhone,
  handlePhoneInputChange,
};
